// ABOUTME: Cross-customer shipped-orders overview UI (one customer per row, daily bars).
// ABOUTME: Top bar range controls, KPI strip, and a scrolling list of customer bar charts.

import { useMemo, useState } from "react";
import { Box, useTheme, InputBase, ButtonBase, Avatar, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../theme";
import { useGetAllCustomersOrderHistoryQuery } from "../../../../newapi/software/softwareOrdersSlice";
import { trendPct, formatNL, strongest, barHeightPct, monthLabel } from "../ordersDerive";
import StatCard from "../../../../components/theme/cards/StatCard";
import Text from "../../../../components/theme/text/Text";
import { DropdownBase } from "../../../../components/theme/dropdowns/Dropdown";
import ShTextSkeleton from "../../../../components/theme/skeleton/ShTextSkeleton";

// Fixed height of a row's chart track. Bars grow from a baseline within this;
// the value label sits in the reserved space above each bar.
const CHART_HEIGHT = 80;
const LABEL_SPACE = 16;
// Cap the bar-chart width so bars stay readable on ultra-wide screens; rows stay full-width.
const CHART_MAX_WIDTH = 1120;       // the bar-chart area (per row + the shared ruler)

// Minimum order volume for a customer to count toward the top KPI stats,
// per selected range. Sub-threshold (low-volume) fulfilments are ignored in the
// totals / active count / strongest riser+faller, but still shown in the row list.
const KPI_MIN_ORDERS = { "30": 500, month: 500, "7": 100 };

const All = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();
    const navigate = useNavigate();

    // grey[0] only exists in light mode; mirror Index.jsx's mode-aware surface color.
    const surface = theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0];

    const now = new Date();
    const [rangeMode, setRangeMode] = useState("30");          // "30" | "7" | "month"
    const [monthState, setMonthState] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
    const [search, setSearch] = useState("");

    // Range options for the house DropdownBase control (Tijdspan).
    const rangeOptions = [
        { id: 0, value: "30", column: "software_orders_last_30" },
        { id: 1, value: "7", column: "software_orders_last_7" },
        { id: 2, value: "month", column: "software_orders_month" },
    ];
    const activeRange = rangeOptions.find(o => o.value === rangeMode) ?? rangeOptions[0];

    const queryArgs = rangeMode === "month"
        ? { range: "month", year: monthState.year, month: monthState.month }
        : { range: rangeMode };

    const { data, isFetching } = useGetAllCustomersOrderHistoryQuery(queryArgs);

    const days = useMemo(() => data?.days ?? [], [data]);
    const customers = useMemo(() => data?.customers ?? [], [data]);

    const filtered = useMemo(
        () => customers.filter(c => c.name.toLowerCase().includes(search.trim().toLowerCase())),
        [customers, search]
    );

    // Top KPI stats ignore low-volume fulfilments (under the per-range threshold).
    // The row list below still shows every customer.
    const kpiCustomers = useMemo(() => {
        const minOrders = KPI_MIN_ORDERS[rangeMode] ?? KPI_MIN_ORDERS["30"];
        return customers.filter(c => c.total >= minOrders);
    }, [customers, rangeMode]);

    const totalShipped = useMemo(() => kpiCustomers.reduce((s, c) => s + c.total, 0), [kpiCustomers]);
    const prevTotalShipped = useMemo(() => kpiCustomers.reduce((s, c) => s + c.prevTotal, 0), [kpiCustomers]);
    const overallTrend = trendPct(totalShipped, prevTotalShipped);
    const { stijger, daler } = useMemo(() => strongest(kpiCustomers), [kpiCustomers]);

    const isCurrentMonth = monthState.year === now.getFullYear() && monthState.month === (now.getMonth() + 1);

    const stepMonth = (delta) => {
        let y = monthState.year, m = monthState.month + delta;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth() + 1)) return;
        setMonthState({ year: y, month: m });
    };

    const goToCustomer = (customer) => {
        // Drill into the customer's existing "Bestellingen verzonden" dashboard.
        // NOTE: exact environment-context-switch mechanism is finalized in a follow-up;
        // this routes to the environments view with the target customer id in router state.
        navigate(`/software/environments`, { state: { openCustomerId: customer.id } });
    };

    const headerText = `${t("software_orders_per_customer")} · ${t("software_orders_sorted_total")} · ${filtered.length} ${t("software_orders_customers")}`;

    return (
        <Box sx={{ width: 1, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Header area on the tertiary background: top bar + KPI strip */}
            <Box sx={{ px: 4, pb: 3, display: "flex", flexDirection: "column", gap: 2, "@media screen and (max-width: 47.9375em)": { px: 3 } }}>
            {/* Top bar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ fontWeight: 700 }}>
                    {t("software_orders_breadcrumb")} <span style={{ opacity: 0.5 }}>› {t("software_orders_all_customers")}</span>
                </Box>
                <Box sx={{ flex: 1 }} />

                {/* Search — matches the topbar search input */}
                <Box
                    sx={{
                        display: "flex",
                        borderRadius: 5,
                        background: colors.grey[200],
                    }}
                >
                    <InputBase
                        sx={{ px: 2, height: 40, lineHeight: "40px", minWidth: 220 }}
                        placeholder={t("software_orders_search_placeholder")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Box>

                {/* Month stepper — only when "Maand" is the selected range */}
                {rangeMode === "month" && (
                    <Box
                        sx={{
                            height: 40,
                            px: 0.5,
                            borderRadius: 5,
                            display: "flex",
                            alignItems: "center",
                            background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                            boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                        }}
                    >
                        <ButtonBase
                            onClick={() => stepMonth(-1)}
                            sx={{ width: 32, height: 32, borderRadius: 4, color: colors.txt["secondary"] }}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </ButtonBase>
                        <Text bold sx={{ minWidth: 96, textAlign: "center" }}>{monthLabel(monthState.year, monthState.month)}</Text>
                        <ButtonBase
                            onClick={() => stepMonth(1)}
                            disabled={isCurrentMonth}
                            sx={{ width: 32, height: 32, borderRadius: 4, color: colors.txt["secondary"], opacity: isCurrentMonth ? 0.35 : 1 }}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </ButtonBase>
                    </Box>
                )}

                {/* Range (Tijdspan) — house dropdown pill control */}
                <Box
                    sx={{
                        height: 40,
                        px: 0.5,
                        borderRadius: 5,
                        display: "flex",
                        alignItems: "center",
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                    }}
                >
                    <Box
                        sx={{
                            height: 32,
                            lineHeight: "32px",
                            px: 1.5,
                            borderRadius: 4,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            mr: 1.5,
                        }}
                    >
                        <Text>{t("software_orders_timespan")}</Text>
                    </Box>
                    <Text sx={{ pr: 2 }} bold>{t(activeRange.column)}</Text>
                    <DropdownBase
                        key={activeRange.id}
                        displayName={"column"}
                        onChange={(selected) => setRangeMode(rangeOptions[selected].value)}
                        useTranslation
                        options={rangeOptions}
                        selected={activeRange.id}
                        closeOnSelection
                        maxHeight={"unset"}
                        valueKey={"id"}
                        disallowDeselection
                    >
                        <ButtonBase
                            sx={{
                                ml: "auto",
                                width: 32,
                                height: 32,
                                background: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                                color: "#fff",
                                borderRadius: 5,
                            }}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </ButtonBase>
                    </DropdownBase>
                </Box>
            </Box>

            {/* KPI strip */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
                <StatCard isLoading={isFetching} label={t("software_orders_kpi_total")} value={formatNL(totalShipped)} trend={overallTrend} color="lightblue" icon={<LocalShippingOutlinedIcon className="logo" />} />
                <StatCard isLoading={isFetching} label={t("software_orders_kpi_active")} value={String(kpiCustomers.length)} color="blue" icon={<GroupsOutlinedIcon className="logo" />} />
                <StatCard isLoading={isFetching} label={t("software_orders_kpi_riser")} value={stijger ? stijger.name : "—"} trend={stijger ? trendPct(stijger.total, stijger.prevTotal) : null} color="green" icon={<TrendingUpIcon className="logo" />} />
                <StatCard isLoading={isFetching} label={t("software_orders_kpi_faller")} value={daler ? daler.name : "—"} trend={daler ? trendPct(daler.total, daler.prevTotal) : null} color="red" icon={<TrendingDownIcon className="logo" />} />
            </Box>
            </Box>

            {/* White content surface, full-bleed with rounded shoulder notches */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    background: surface,
                    position: "relative",
                    zIndex: 2,
                    pt: 4,
                    px: 4,
                    "&:before": {
                        content: "''", display: "block", position: "absolute", left: 0, top: 0,
                        width: "24px", height: "48px", zIndex: 1, backgroundColor: surface,
                        borderTopLeftRadius: "24px", boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`, transition: "250ms",
                    },
                    "&:after": {
                        content: "''", display: "block", position: "absolute", right: 0, top: 0,
                        width: "24px", height: "48px", zIndex: 1, backgroundColor: surface,
                        borderTopLeftRadius: "24px", boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                        transform: "scaleX(-1)", transition: "250ms",
                    },
                    "@media screen and (max-width: 47.9375em)": { px: 3 },
                }}
            >
                {/* Section header */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box sx={{ fontWeight: 600, color: colors.txt["secondary"] }}>{headerText}</Box>
                </Box>

                {/* Rows are full-width; only the chart bars are locked to CHART_MAX_WIDTH. */}
                {/* Sticky date ruler */}
                <Box sx={{ position: "sticky", top: 0, zIndex: 3, display: "flex", background: surface, py: 1 }}>
                    <Box sx={{ width: 230, flexShrink: 0, fontWeight: 600, color: colors.txt["secondary"] }}>{t("software_orders_customer")}</Box>
                    {/* Spacer matching the per-row SubscriptionCounts column so the day labels line up with the bars */}
                    <Box sx={{ width: 200, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, maxWidth: CHART_MAX_WIDTH, display: "flex", gap: "2px" }}>
                        {days.map((d) => (
                            <Box key={d.date} sx={{ flex: 1, textAlign: "center", fontSize: 10, color: colors.txt["secondary"] }}>{d.label}</Box>
                        ))}
                    </Box>
                </Box>

                {/* Rows */}
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    {isFetching && <LoadingRows colors={colors} dayCount={Math.max(days.length, 30)} />}
                    {!isFetching && filtered.length === 0 && (<Box sx={{ p: 3, color: colors.txt["secondary"] }}>{t("software_orders_none_found")}</Box>)}
                    {!isFetching && filtered.map((c, idx) => {
                        const t30 = trendPct(c.total, c.prevTotal);
                        const rowMax = Math.max(0, ...c.series);
                        return (
                            <Box key={c.id} onClick={() => goToCustomer(c)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToCustomer(c); } }}
                                sx={{ display: "flex", alignItems: "center", py: 2.5, cursor: "pointer", borderTop: `1px solid ${colors.grey[200]}`, "&:hover": { background: colors.grey[100] } }}>
                                {/* Left info column */}
                                <Box sx={{ width: 230, flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box sx={{ width: 20, textAlign: "right", fontWeight: idx < 3 ? 700 : 400 }}>{idx + 1}</Box>
                                    <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: c.color }}>{c.initials}</Avatar>
                                    <Box sx={{ overflow: "hidden" }}>
                                        <Box title={c.name} sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>{c.name}</Box>
                                        <Box sx={{ fontSize: 12, display: "flex", gap: 1, alignItems: "center" }}>
                                            <span>{formatNL(c.total)}</span>
                                            <TrendPill trend={t30} />
                                        </Box>
                                    </Box>
                                </Box>
                                {/* Active subscription counts per catalog */}
                                <SubscriptionCounts customer={c} />
                                {/* Chart column — CSS bars contained in a fixed-height track, aligned under the shared ruler */}
                                <Box sx={{ flex: 1, maxWidth: CHART_MAX_WIDTH, height: CHART_HEIGHT, display: "flex", alignItems: "flex-end", gap: "2px" }}>
                                    {c.series.map((v, i) => {
                                        const isWeekend = days[i]?.isWeekend;
                                        return (
                                            <Tooltip key={i} arrow title={`${days[i]?.label ?? ""} · ${formatNL(v)}`}>
                                                <Box sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    height: "100%",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "flex-end",
                                                    alignItems: "center",
                                                }}>
                                                    {/* value label sits in the reserved space above the bar */}
                                                    <Box sx={{
                                                        height: LABEL_SPACE,
                                                        fontSize: 9,
                                                        lineHeight: `${LABEL_SPACE}px`,
                                                        color: colors.txt["secondary"],
                                                        whiteSpace: "nowrap",
                                                    }}>
                                                        {v > 0 ? formatNL(v) : ""}
                                                    </Box>
                                                    <Box sx={{
                                                        width: "80%",
                                                        maxWidth: 30,
                                                        height: `${(barHeightPct(v, rowMax) / 100) * (CHART_HEIGHT - LABEL_SPACE)}px`,
                                                        minHeight: v > 0 ? 3 : 0,
                                                        borderRadius: "3px 3px 0 0",
                                                        background: c.color,
                                                        opacity: isWeekend ? 0.5 : 1,
                                                        transition: "height 200ms",
                                                    }} />
                                                </Box>
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

// Skeleton placeholder shown while the cross-customer data is loading,
// mirroring the row layout (left info column + a track of bars).
const LoadingRows = ({ colors, dayCount }) => {
    const rows = Array.from({ length: 7 });
    const bars = Array.from({ length: dayCount });
    // Deterministic varied bar heights so the skeleton reads as a chart, not a flat block.
    const heightFor = (i) => 25 + ((i * 37) % 60);
    return (
        <Box>
            {rows.map((_, r) => (
                <Box key={r} sx={{ display: "flex", alignItems: "center", py: 2.5, borderTop: `1px solid ${colors.grey[200]}` }}>
                    {/* Left info column */}
                    <Box sx={{ width: 230, flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 20 }} />
                        <ShTextSkeleton height={28} sx={{ width: 28, borderRadius: "50%", flexShrink: 0 }} />
                        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <ShTextSkeleton height={14} sx={{ width: "70%" }} />
                            <ShTextSkeleton height={12} sx={{ width: "40%" }} />
                        </Box>
                    </Box>
                    {/* Spacer matching the SubscriptionCounts column so the skeleton bars align with the ruler */}
                    <Box sx={{ width: 200, flexShrink: 0 }} />
                    {/* Bars */}
                    <Box sx={{ flex: 1, maxWidth: CHART_MAX_WIDTH, height: CHART_HEIGHT, display: "flex", alignItems: "flex-end", gap: "2px" }}>
                        {bars.map((__, i) => (
                            <Box key={i} sx={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center", alignItems: "flex-end" }}>
                                <ShTextSkeleton height={heightFor((r + i))} sx={{ width: "80%", maxWidth: 30, borderRadius: "3px 3px 0 0" }} />
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

const TrendPill = ({ trend }) => {
    if (trend === null || trend === undefined) return null;
    const up = trend >= 0;
    return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", fontSize: 12, color: up ? "#1f9d55" : "#e3342f" }}>
            {up ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
            {Math.abs(trend)}%
        </Box>
    );
};

// Compact active-subscription counts per catalog (amazon connections, checkouts, amazon orders, relation labels).
// Older/error catalogs may omit the fields, so each count defaults to 0.
const SubscriptionCounts = ({ customer }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const items = [
        { icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 14 }} />, value: customer.checkouts ?? 0, label: t("software_orders_checkouts") },
        { icon: <StorefrontOutlinedIcon sx={{ fontSize: 14 }} />, value: customer.amazonConnections ?? 0, label: t("software_orders_amazon_connections") },
        { icon: <Inventory2OutlinedIcon sx={{ fontSize: 14 }} />, value: customer.amazonOrders ?? 0, label: t("software_orders_amazon_orders") },
        { icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 14 }} />, value: customer.relationLabels ?? 0, label: t("software_orders_relation_labels") },
    ];

    return (
        <Box sx={{ width: 200, flexShrink: 0, display: "flex", alignItems: "center", gap: 1.5, color: colors.txt["secondary"], fontSize: 12 }}>
            {items.map((item, i) => (
                <Tooltip key={i} arrow title={item.label}>
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        {item.icon}
                        {formatNL(item.value)}
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
};

export default All;
